import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router";
import { appRoutesDefine, useAppRouteId, useAppRouteParams } from "./AppRoutes";
import { API_CONFIG } from "./api/config";
import { auth } from "./firebase";

onAuthStateChanged(auth, (user) => {
  datadogRum.setUser({
    userKind: "null",
    role: "null",
    ...datadogRum.getUser(),
    id: user?.uid ?? "not-logged-in",
    displayName: user?.displayName ?? "not-logged-in",
    email: user?.email ?? "not-logged-in@example.com",
  });
});

export const ddSanitizeRecursive =
  process.env.NODE_ENV === "development"
    ? <T,>(value: T): T => {
        if (value === null || value === undefined) {
          return value;
        }
        if (typeof value === "string") {
          return value.replace(
            /\/(?:Users|Volumes|mnt|home)\/.+\/spreadsheet-ui-frontend\//g,
            "/***/spreadsheet-ui-frontend/"
          ) as T;
        }
        if (Array.isArray(value)) {
          for (let i = 0; i < value.length; i++) {
            value[i] = ddSanitizeRecursive(value[i]);
          }
          return value;
        }
        if (typeof value === "object") {
          if (value.constructor && value.constructor !== Object) {
            return value;
          }
          for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
              (value as any)[key] = ddSanitizeRecursive((value as any)[key]);
            }
          }
          return value;
        }
        return value;
      }
    : <T,>(value: T): T => value;

const shortEnvNames: { [key: string]: string } = {
  production: "prod",
  staging: "stg",
  qa: "qa",
  development: "dev",
};

datadogRum.init({
  applicationId: "541be6c4-66a7-41de-b458-eeb293058150",
  clientToken: "pub1598c8c1086ba9a701e02b56d39e4064",
  site: "ap1.datadoghq.com",
  // NOTE: デプロイ環境では同一オリジンの nginx (env_nginx/server.conf.template) 経由で
  // Datadog に送る。ローカル開発では nginx を通らないため直接送る
  proxy:
    location.protocol === "http:"
      ? undefined
      : ({ path, parameters }) =>
          `${location.origin}/_dd-rum${path}?${parameters}`,
  service: "ssui",
  env:
    location.protocol === "http:"
      ? "local"
      : shortEnvNames[process.env.APP_ENV!] ?? process.env.APP_ENV ?? "unknown",
  version: process.env.RELEASE_VERSION ?? "unknown",
  plugins: [reactPlugin({})],

  actionNameAttribute: "aria-label",
  allowedTracingUrls: [
    location.origin,
    "https://storage.googleapis.com/",
    {
      match: "https://securetoken.googleapis.com/",
      propagatorTypes: [],
    },
    {
      match: API_CONFIG.baseURL.match(/^(https?:\/\/[^/]+)/)?.[1]!,
      propagatorTypes: [],
    },
  ],
  beforeSend: (event, context) => {
    if (!context) return true;
    event = ddSanitizeRecursive(event);
    event.context = event.context || {};
    if (event.type === "resource") {
      if (event.resource.delivery_type === "cache") return false;
      if (event.resource.status_code && event.resource.status_code < 400) {
        return false;
      }
    }
    return true;
  },
  defaultPrivacyLevel: "mask-user-input",
  sessionReplaySampleRate: 0,
  sessionSampleRate: 100,
  trackResources: true,
  trackUserInteractions: true,
  trackViewsManually: true,
});

export const DatadogViewDispatcher = () => {
  // Track every route change with useLocation API
  const routeId = useAppRouteId();
  const location = useLocation();
  const routeName = appRoutesDefine[routeId].path;
  const routeParams = useAppRouteParams();

  useEffect(() => {
    datadogRum.startView({
      name: location.pathname,
      context: {
        routeName,
        routeId,
        routeParams,
      },
    });
  }, [location.pathname]);
  return null;
};

export const defineDDAction =
  (group: string) => (action: string, payload?: any) => {
    datadogRum.addAction(`${group}_${action}`, {
      group,
      payload: payload ?? null,
    });
  };
